import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { catchError, map } from 'rxjs/operators';
import { NotificationService } from '../../../../../common/services';
import { ShipmentTransportOrderEquipmentsService } from './shipment-transport-order-equipments.service';
import { Observable, of } from 'rxjs';
import { Error, CustomHttpErrorResponse, MeasurementUnitCodesEnum } from '../../../../../common';
import {
  ShipmentTransportOrderEquipmentsInitialState,
  ShipmentTransportOrderEquipmentsLoad,
} from './shipment-transport-order-equipments.actions';
import { WaybillEquipmentItemModel, WaybillEquipmentsModel } from '../../../../models/waybill-equipments.model';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface ShipmentTransportOrderEquipmentsStateModel {
  equipments: WaybillEquipmentItemModel[];
  dimensionsUnitCode: MeasurementUnitCodesEnum;
  weightUnitCode: MeasurementUnitCodesEnum;
  volumeUnitCode: MeasurementUnitCodesEnum;
  loaded: boolean;
}

const initialState: ShipmentTransportOrderEquipmentsStateModel = {
  equipments: [],
  dimensionsUnitCode: null,
  weightUnitCode: null,
  volumeUnitCode: null,
  loaded: false,
};

@State<ShipmentTransportOrderEquipmentsStateModel>({
  name: 'shipmentTransportOrderEquipments',
  defaults: initialState,
})
@Injectable()
export class ShipmentTransportOrderEquipmentsState {
  constructor(
    private shipmentTransportOrderEndpointTransportOrderEquipmentsService: ShipmentTransportOrderEquipmentsService,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getState(state: ShipmentTransportOrderEquipmentsStateModel): ShipmentTransportOrderEquipmentsStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: ShipmentTransportOrderEquipmentsStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getEquipments(state: ShipmentTransportOrderEquipmentsStateModel): WaybillEquipmentItemModel[] {
    return state.equipments;
  }

  @Selector()
  static getDimensionsUnitCode(state: ShipmentTransportOrderEquipmentsStateModel): MeasurementUnitCodesEnum {
    return state.dimensionsUnitCode;
  }

  @Selector()
  static getVolumeUnitCode(state: ShipmentTransportOrderEquipmentsStateModel): MeasurementUnitCodesEnum {
    return state.volumeUnitCode;
  }

  @Selector()
  static getWeightUnitCode(state: ShipmentTransportOrderEquipmentsStateModel): MeasurementUnitCodesEnum {
    return state.weightUnitCode;
  }

  @Action(ShipmentTransportOrderEquipmentsLoad)
  getTransportOrderEquipments(
    ctx: StateContext<ShipmentTransportOrderEquipmentsStateModel>,
    { payload }: ShipmentTransportOrderEquipmentsLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.shipmentTransportOrderEndpointTransportOrderEquipmentsService
      .getShipmentTransportOrderTransportEquipments(payload)
      .pipe(
        map((response: WaybillEquipmentsModel) => {
          ctx.patchState({
            equipments: [...response.items],
            dimensionsUnitCode: response.dimensionsUnitCode,
            volumeUnitCode: response.volumeUnitCode,
            weightUnitCode: response.weightUnitCode,
            loaded: true,
          });
        }),
        catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
      );
  }

  @Action(ShipmentTransportOrderEquipmentsInitialState)
  setInitialState(ctx: StateContext<ShipmentTransportOrderEquipmentsStateModel>): void {
    ctx.setState(initialState);
  }
}
